import { useRef, useState } from 'react'
import { AlertTriangle, FileText, Upload, UploadCloud, X } from 'lucide-react'
import { Button, Chip, Drawer, Select, Textarea, toast } from '@/components/ui'
import { useClassifyShippingDocuments, useUploadShippingDocumentsBatch } from '../api/shipping-docs-queries'
import type { ShippingDocType } from '../api/shipping-docs-types'

const MAX_BYTES = 25 * 1024 * 1024
const MAX_FILES = 50
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.xlsx,.docx,application/pdf,image/png,image/jpeg'
const EXT_OK = /\.(pdf|png|jpe?g|xlsx|docx)$/i

export interface UploadShippingDocumentDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dispatchId: number
  dispatchLabel: string
  types: ShippingDocType[]
}

interface Row {
  id: number
  file: File
  typeKey: string
  /** 'medium' = a weaker guess worth a glance; null = the server could not tell, or the person chose. */
  confidence: 'high' | 'medium' | null
  error: string | null
}

const size = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

/**
 * File a whole shipment at once: drop or select all the files, the types are suggested from each file's name and
 * first page, fix any that are wrong or blank, then file them in one click. Parent remounts with a new `key`
 * per opening.
 */
export function UploadShippingDocumentDrawer({
  open,
  onOpenChange,
  dispatchId,
  dispatchLabel,
  types,
}: UploadShippingDocumentDrawerProps) {
  const classify = useClassifyShippingDocuments()
  const upload = useUploadShippingDocumentsBatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const nextId = useRef(1)
  const [rows, setRows] = useState<Row[]>([])
  const [notes, setNotes] = useState('')
  const [dragging, setDragging] = useState(false)

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list)
    if (incoming.length === 0) return
    if (rows.length + incoming.length > MAX_FILES) {
      toast.danger(`At most ${MAX_FILES} files at a time`)
      return
    }
    const fresh: Row[] = incoming.map((file) => ({
      id: nextId.current++,
      file,
      typeKey: '',
      confidence: null,
      error: !EXT_OK.test(file.name)
        ? 'Use a PDF, PNG, JPEG, XLSX or DOCX file.'
        : file.size > MAX_BYTES
          ? 'File is larger than 25 MB.'
          : null,
    }))
    setRows((cur) => [...cur, ...fresh])
    const toClassify = fresh.filter((r) => !r.error)
    if (toClassify.length === 0) return
    classify.mutate(
      toClassify.map((r) => r.file),
      {
        onSuccess: (guesses) =>
          setRows((cur) =>
            cur.map((r) => {
              const i = toClassify.findIndex((x) => x.id === r.id)
              const g = i >= 0 ? guesses[i] : undefined
              // never overwrite a type the person already picked while the guess was on its way
              if (!g || !g.type_key || r.typeKey || !types.some((t) => t.key === g.type_key)) return r
              return { ...r, typeKey: g.type_key, confidence: g.confidence }
            }),
          ),
        onError: () => toast.info('Could not suggest types. Choose them yourself.'),
      },
    )
  }

  const setType = (id: number, typeKey: string) =>
    setRows((cur) => cur.map((r) => (r.id === id ? { ...r, typeKey, confidence: null } : r)))
  const remove = (id: number) => setRows((cur) => cur.filter((r) => r.id !== id))

  const blocked = rows.some((r) => r.error && !r.typeKey) // unusable files must be removed
  const missing = rows.filter((r) => !r.typeKey).length
  const canSubmit = rows.length > 0 && missing === 0 && !rows.some((r) => r.error) && !upload.isPending && !blocked

  const submit = () => {
    if (!canSubmit) return
    upload.mutate(
      { dispatchId, files: rows.map((r) => r.file), typeKeys: rows.map((r) => r.typeKey), notes: notes.trim() || undefined },
      {
        onSuccess: (res) => {
          const failed = new Map(res.results.filter((x) => !x.ok).map((x) => [x.filename, x.error ?? 'Failed']))
          const dup = res.results.filter((x) => x.ok && x.unchanged).length
          if (failed.size === 0) {
            toast.success(
              `${res.filed} document${res.filed === 1 ? '' : 's'} filed` + (dup ? ` (${dup} already filed)` : ''),
            )
            onOpenChange(false)
            return
          }
          // keep only the ones that failed, with the reason, so they can be fixed and retried
          setRows((cur) =>
            cur.filter((r) => failed.has(r.file.name)).map((r) => ({ ...r, error: failed.get(r.file.name) ?? null })),
          )
          toast.danger(`${failed.size} file${failed.size === 1 ? '' : 's'} not filed`, {
            description: `${res.filed} filed. Fix or remove the rest and try again.`,
          })
        },
        onError: (err) => toast.danger('Upload failed', { description: (err as Error).message }),
      },
    )
  }

  return (
    <Drawer
      surface="form"
      open={open}
      onOpenChange={onOpenChange}
      title="Upload shipping documents"
      meta={dispatchLabel}
      footer={
        <div className="flex items-center justify-end gap-3">
          {rows.length > 0 && missing > 0 ? (
            <span className="mr-auto text-sm text-warning-text">
              {missing} file{missing === 1 ? '' : 's'} still need a type
            </span>
          ) : null}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit} loading={upload.isPending}>
            <Upload size={14} /> {rows.length > 1 ? `File ${rows.length} documents` : 'File document'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <div
          role="button"
          tabIndex={0}
          aria-label="Add files"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            addFiles(e.dataTransfer.files)
          }}
          className={`flex cursor-pointer flex-col items-center gap-1 rounded-md border-2 border-dashed p-6 text-center ${
            dragging ? 'border-accent bg-accent-subtle' : 'border-default'
          }`}
        >
          <UploadCloud size={24} />
          <span className="text-md font-semibold text-primary">Drop the whole shipment here, or click to choose files</span>
          <span className="text-sm text-secondary">PDF, PNG, JPEG, XLSX or DOCX, up to 25 MB each. Types are suggested for you.</span>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            aria-label="Files"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </div>

        {classify.isPending ? <span className="text-sm text-secondary">Reading the files to suggest types…</span> : null}

        {rows.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {rows.map((r) => (
              <li
                key={r.id}
                className={`flex flex-col gap-1 rounded-md border p-2 ${r.error ? 'border-danger' : !r.typeKey ? 'border-warning' : 'border-default'}`}
              >
                <div className="flex items-center gap-2">
                  <FileText size={14} className="shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-md" title={r.file.name}>
                    {r.file.name}
                  </span>
                  <span className="shrink-0 text-sm text-secondary">{size(r.file.size)}</span>
                  <Button variant="ghost" size="sm" iconOnly aria-label={`Remove ${r.file.name}`} onClick={() => remove(r.id)}>
                    <X size={14} />
                  </Button>
                </div>
                {r.error ? (
                  <span className="flex items-center gap-1 text-sm text-danger-text">
                    <AlertTriangle size={12} /> {r.error}
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <Select
                      aria-label={`Type of ${r.file.name}`}
                      value={r.typeKey}
                      onChange={(e) => setType(r.id, e.target.value)}
                    >
                      <option value="">Choose a type…</option>
                      {types.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </Select>
                    {r.confidence === 'medium' ? <Chip variant="warning">Check this one</Chip> : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Notes for all of them (optional)</span>
          <Textarea aria-label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
    </Drawer>
  )
}
